import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSchedule } from '../api';
import PageHeader from '../components/PageHeader';
import MatchList from '../components/MatchList';
import LiveBanner from '../components/LiveBanner';
import { SkeletonList } from '../components/Skeleton';
import { TZ } from '../format';

type Filter = 'all' | 'upcoming' | 'completed';

export default function Schedule() {
  const { data, isLoading } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule });
  const [filter, setFilter] = useState<Filter>('upcoming');

  const matches = useMemo(() => {
    const all = data?.matches ?? [];
    if (filter === 'upcoming') return all.filter((m) => m.status !== 'post');
    if (filter === 'completed') return all.filter((m) => m.status === 'post');
    return all;
  }, [data, filter]);

  return (
    <>
      <PageHeader
        title="Schedule"
        subtitle={`All times ${TZ}.`}
        right={
          <div className="flex rounded-lg bg-ink-800 p-1">
            <FilterBtn current={filter} value="upcoming" onClick={setFilter} label="Upcoming">
              <ClockIcon />
            </FilterBtn>
            <FilterBtn current={filter} value="completed" onClick={setFilter} label="Completed">
              <CheckIcon />
            </FilterBtn>
            <FilterBtn current={filter} value="all" onClick={setFilter} label="All">
              <LayersIcon />
            </FilterBtn>
          </div>
        }
      />
      <LiveBanner />
      <div className="px-4 lg:px-0">
        {isLoading ? (
          <SkeletonList count={6} height="h-24" />
        ) : (
          <MatchList matches={matches} showOwners emptyText="No matches in this filter." />
        )}
      </div>
    </>
  );
}

function FilterBtn({ current, value, onClick, label, children }: {
  current: Filter; value: Filter; onClick: (f: Filter) => void; label: string; children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
        active ? 'bg-ink-700 text-chalk' : 'text-dim hover:text-chalk'
      }`}
    >
      {children}
    </button>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="4 12 10 18 20 6" />
    </svg>
  );
}
function LayersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 22 8 12 14 2 8 12 2" />
      <polyline points="2 14 12 20 22 14" />
    </svg>
  );
}
